// This Component displays the row and manages the compare popup state

import {
  Dispatch,
  MouseEvent,
  SetStateAction,
  useContext,
  useEffect,
  useMemo,
  useRef,
} from 'react';
import clsx from 'clsx';
import * as R from 'ramda';

import PercentBar from '@atom/components/common/percentBar/PercentBar';
import { usePreferences } from '@atom/hooks/usePreferences';
import {
  ClickAwayListener,
  Icon,
  IconButton,
  ListTable,
  Tooltip,
} from '@atom/mui';
import colors from '@atom/styles/colors';
import { BudgetCategory, BudgetItem } from '@atom/types/budget';
import { numberToLocaleString } from '@atom/utilities/currencyUtility';
import { isNilOrEmpty } from '@atom/utilities/validationUtilities';

import BudgetDetailComparison from '../BudgetDetailComparison';
import BudgetDetailContext from '../BudgetDetailContext';
import {
  BUDGET_COL_WIDTH,
  COMMENT_COL_WIDTH,
  COST_TYPE_COL_WIDTH,
  DAMAGE_CLAIM_EXPENSE_TOOLTIP_COPY,
  DEDICATED_CREDIT_TOOLTIP_COPY,
  EditField,
  FIXED_COST_TOOLTIP_COPY,
  getComparisonIcon,
  ICON_COL_WIDTH,
} from '../budgetDetailUtils';

import classes from '../budgetDetail.module.css';

const BLANK_CHAR = '\u00A0';

const { TableRow, TableCell } = ListTable;

interface Props {
  budgetItem: BudgetItem;
  budgetCategory: BudgetCategory;
  handleInputCellClick: (field: EditField, budgetItem: BudgetItem) => void;
  loadingItemUpdate: boolean;
  handleInputValueChange: (event, prevTotal) => void;
  handleKeyUp: (event) => void;
  handleSaveField: () => void;
  editValue: string;
  setEditValue: Dispatch<SetStateAction<string>>;
}

const styles = {
  costTypeCell: {
    paddingLeft: '0',
  },
  nameCell: {
    paddingLeft: '2rem',
  },
  itemCell: {
    padding: 0,
  },
  celInfoIconButton: {
    padding: '0',
    height: '1.25rem',
  },
  celInfoIcon: { fontSize: '0.75rem' },
  valueCellEditing: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.15rem',
  },
  input: {
    width: '95%',
    height: '100%',
    border: `1px solid ${colors.utility.activeBlue}`,
    borderRadius: '0.25rem',
    padding: '0.5rem 0.15rem',
  },
  budgetCommentCell: {
    padding: 0,
    width: '95%',
  },
};

const BudgetDetailItemRow = ({
  budgetItem,
  budgetCategory,
  handleInputCellClick,
  loadingItemUpdate,
  handleInputValueChange,
  handleKeyUp,
  handleSaveField,
  editValue,
  setEditValue,
}: Props) => {
  const {
    budgetSummaries,
    comparisonBudgets,
    openComparisonRowId,
    setOpenComparisonRowId,
    parentBudgetUnit,
    editingField,
    editingItem,
    setEditingItem,
    fetchSummaries,
    loadingSummaries,
    itemHoverId,
    setItemHoverId,
    showTracking,
    showExpenditures,
    budgetTypes,
  } = useContext(BudgetDetailContext);

  const preferences = usePreferences();
  const hideCostType = R.pathOr(
    false,
    ['budgeting', 'hideCostType'],
    preferences,
  );

  const isDedicatedCredit = useMemo(
    () => showExpenditures && budgetItem?.costType === 'Dedicated Credit',
    [budgetItem, showExpenditures],
  );

  const isDamageClaimExpense = useMemo(
    () => showExpenditures && budgetItem?.name === 'Damage Claim Expense',
    [budgetItem, showExpenditures],
  );

  const isFixedCost = useMemo(
    () => budgetItem?.costType === 'Fixed Cost',
    [budgetItem],
  );

  const open: boolean = useMemo(
    () => openComparisonRowId === budgetItem?.id,
    [openComparisonRowId],
  );

  const hovering: boolean = useMemo(
    () => itemHoverId === budgetItem?.id,
    [itemHoverId],
  );

  const anchorRef = useRef<HTMLDivElement>(null);
  const commentInputRef = useRef<HTMLInputElement>(null);
  const budgetInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!isNilOrEmpty(commentInputRef.current)) {
      // @ts-ignore
      commentInputRef.current?.select();
    }
    if (!isNilOrEmpty(budgetInputRef.current)) {
      // @ts-ignore
      budgetInputRef.current?.select();
    }
  }, [commentInputRef.current, budgetInputRef.current]);

  useEffect(() => {
    if (open && !isNilOrEmpty(comparisonBudgets)) {
      fetchSummaries({
        variables: {
          input: {
            budgetUnitId: parentBudgetUnit?.id,
            budgetIds: R.pluck('id', comparisonBudgets),
            categoryIds: [budgetCategory?.id],
            itemNames: [budgetItem.name],
            budgetTypes,
          },
        },
      });
    }
  }, [open, comparisonBudgets]);

  const handleCompareClick = (event: MouseEvent) => {
    event.stopPropagation();
    setOpenComparisonRowId(open ? null : budgetItem?.id);
  };

  const handleOutsideClickCancelEdit = event => {
    event.stopPropagation();
    setEditingItem(null);
  };

  const handleOutsideClickCloseComparison = event => {
    event.stopPropagation();
    // Close if user is not hovering over the current comparison row
    // - this allows editing the row while leaving the popup open
    if (isNilOrEmpty(itemHoverId) || itemHoverId !== openComparisonRowId) {
      setOpenComparisonRowId(null);
    }
  };

  return (
    <>
      <TableRow
        onMouseEnter={() => setItemHoverId(budgetItem?.id)}
        onMouseLeave={() => setItemHoverId(null)}
        hovercolor={colors.utility.lightBlueHighlight}
        style={{
          backgroundColor: open
            ? colors.utility.lightBlueHighlight
            : colors.neutral.white,
        }}
      >
        <TableCell style={{ width: '100%' }}>
          <span className={classes.budgetTableCell} style={styles.nameCell}>
            <span>{budgetItem?.name}</span>
            {isDamageClaimExpense && (
              <IconButton
                tooltip={DAMAGE_CLAIM_EXPENSE_TOOLTIP_COPY}
                style={styles.celInfoIconButton}
              >
                <Icon style={styles.celInfoIcon}>info_outlined</Icon>
              </IconButton>
            )}
          </span>
        </TableCell>
        {!hideCostType && (
          <TableCell width={COST_TYPE_COL_WIDTH} style={styles.costTypeCell}>
            <span className={classes.budgetTableCell} style={styles.itemCell}>
              <span>{budgetItem.costType}</span>
              {isDedicatedCredit && (
                <IconButton
                  tooltip={DEDICATED_CREDIT_TOOLTIP_COPY}
                  style={styles.celInfoIconButton}
                >
                  <Icon style={styles.celInfoIcon}>info_outlined</Icon>
                </IconButton>
              )}
              {hovering && isFixedCost && (
                <IconButton
                  tooltip={`Fixed Cost: ${FIXED_COST_TOOLTIP_COPY}`}
                  style={styles.celInfoIconButton}
                >
                  <Icon style={styles.celInfoIcon}>info_outlined</Icon>
                </IconButton>
              )}
            </span>
          </TableCell>
        )}

        <TableCell
          onClick={() => handleInputCellClick(EditField.VALUE, budgetItem)}
          className={classes.clickable}
          align="right"
          width={BUDGET_COL_WIDTH}
        >
          <span className={classes.budgetTableCell} style={styles.itemCell}>
            {!loadingItemUpdate &&
            editingField === EditField.VALUE &&
            editingItem?.id === budgetItem?.id ? (
              <ClickAwayListener onClickAway={handleOutsideClickCancelEdit}>
                <span style={styles.valueCellEditing}>
                  $
                  <input
                    ref={budgetInputRef}
                    style={{ ...styles.input, ...{ textAlign: 'right' } }}
                    onChange={event =>
                      handleInputValueChange(event, budgetItem?.budgetValue)
                    }
                    onKeyUp={event => handleKeyUp(event)}
                    onBlur={handleSaveField}
                    value={editValue}
                  />
                </span>
              </ClickAwayListener>
            ) : (
              <span className={classes.editHover}>
                {numberToLocaleString(budgetItem?.budgetValue)}
              </span>
            )}
          </span>
        </TableCell>
        {showTracking && (
          <>
            <TableCell align="right" width={BUDGET_COL_WIDTH}>
              <span className={classes.budgetTableCell}>
                {numberToLocaleString(budgetItem?.actualBudget)}
              </span>
            </TableCell>
            <TableCell align="right" width={BUDGET_COL_WIDTH}>
              <span className={classes.budgetTableCell}>
                {numberToLocaleString(budgetItem?.fixedCosts)}
              </span>
            </TableCell>
            <TableCell align="right" width={BUDGET_COL_WIDTH}>
              <PercentBar
                value={budgetItem?.remaining}
                limit={budgetItem?.budgetValue}
              />
            </TableCell>
          </>
        )}
        <TableCell
          onClick={() => handleInputCellClick(EditField.COMMENT, budgetItem)}
          className={classes.clickable}
          width={COMMENT_COL_WIDTH}
        >
          <span
            className={classes.budgetTableCell}
            style={styles.budgetCommentCell}
          >
            {!loadingItemUpdate &&
            editingField === EditField.COMMENT &&
            editingItem?.id === budgetItem?.id ? (
              <ClickAwayListener onClickAway={handleOutsideClickCancelEdit}>
                <input
                  ref={commentInputRef}
                  style={styles.input}
                  value={editValue}
                  onChange={event => setEditValue(event.target.value)}
                  onKeyUp={event => handleKeyUp(event)}
                  onBlur={handleSaveField}
                />
              </ClickAwayListener>
            ) : (
              <Tooltip title={budgetItem?.comment}>
                <span className={clsx(classes.textNoWrap, classes.editHover)}>
                  {budgetItem?.comment || BLANK_CHAR}
                </span>
              </Tooltip>
            )}
          </span>
        </TableCell>
        <TableCell width={ICON_COL_WIDTH}>
          <span
            ref={anchorRef}
            onClick={handleCompareClick}
            className={clsx(classes.budgetTableCell, classes.clickable)}
          >
            {getComparisonIcon(itemHoverId === budgetItem?.id, open)}
          </span>
        </TableCell>
      </TableRow>
      {open && (
        <BudgetDetailComparison
          handleCloseModal={() => setOpenComparisonRowId(null)}
          loadingSummaries={loadingSummaries}
          budgetSummaries={budgetSummaries}
          includeComments
          anchorEl={anchorRef?.current}
          onClickAway={handleOutsideClickCloseComparison}
        />
      )}
    </>
  );
};

export default BudgetDetailItemRow;
